import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import arrayMutators from "final-form-arrays";
import { pick } from "lodash";
import React, { useMemo, useCallback } from "react";
import { FormProps, withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import {
  FileParameter,
  IResourceLibrary,
  IResourceLibraryItem,
} from "services/KnowfullyAdminClient";
import FileField from "components/FileField";
import SelectResourceLibraryItemType from "./SelectResourceLibraryItemType";
import SelectResourceLibrary from "./SelectResourceLibrary";
import { parseInteger } from "utils/formUtils";
import { FORM_ERROR } from "final-form";
import { standardDateFormat } from "utils/dateUtils";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  subtitle: {
    margin: theme.spacing(3, 0, 2),
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  checkboxContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  lineContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  info: {
    flexGrow: 1,
    textAlign: "right",
    margin: theme.spacing(0, 2),
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
}));

type SaveResourceLibraryItemFormValues = Partial<
  Omit<IResourceLibraryItem, "file" | "resourceLibraryTypeName">
> & {
  file?: File;
  resourceLibraryId: string;
};

export type SaveResourceLibraryItemRequest = Partial<
  Omit<IResourceLibraryItem, "file" | "resourceLibraryTypeName">
> & {
  file?: FileParameter;
  resourceLibraryId: string;
};

const { Form } = withTypes<SaveResourceLibraryItemFormValues>();

type ResourceLibraryItemFormProps = {
  resourceLibrary?: IResourceLibrary;
  resourceLibraryItem?: IResourceLibraryItem;
  submitLabel: string;
  onSubmit: (values: SaveResourceLibraryItemRequest) => void;
  onCancel?: () => void;
  isInsert?: boolean;
};

const ResourceLibraryItemForm = ({
  resourceLibraryItem,
  resourceLibrary,
  submitLabel,
  onCancel,
  onSubmit,
  isInsert,
}: ResourceLibraryItemFormProps &
  FormProps<SaveResourceLibraryItemRequest>) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());

  const init = useMemo(
    () => ({
      ...pick(
        {
          resourceLibraryId: resourceLibrary?.id ?? undefined,
          ...resourceLibraryItem,
        },
        [
          "resourceLibraryId",
          "resourceLibraryTypeId",
          "name",
          "publicationDate",
          "description",
          "sequence",
        ]
      ),
    }),
    [resourceLibraryItem, resourceLibrary?.id]
  );

  const onInnerSubmit = useCallback(
    (values: SaveResourceLibraryItemFormValues) => {
      let file = undefined;
      if (values.file) {
        file = {
          fileName: values.file.name,
          data: values.file,
        };
      }
      return onSubmit({
        ...values,
        file,
        fileName: resourceLibraryItem?.fileName,
        id: resourceLibraryItem?.id,
      });
    },
    [onSubmit, resourceLibraryItem?.fileName, resourceLibraryItem?.id]
  );

  return useMemo(
    () => (
      <Form
        onSubmit={onInnerSubmit}
        initialValues={init}
        mutators={{ ...arrayMutators }}
        validate={(values) => {
          const errors = {};
          if (!values.file && isInsert) {
            const errorMessage = t({
              id: "resourceLibraryFormUploadRequiredLabel",
            });
            return {
              [FORM_ERROR]: errorMessage,
              file: errorMessage,
            };
          } else if (
            values.file &&
            values.file.name.toLowerCase().includes("exe")
          ) {
            const errorMessage = t({
              id: "resourceLibraryFormUploadFileTypeErrorLabel",
            });
            return {
              [FORM_ERROR]: errorMessage,
              file: errorMessage,
            };
          }
          return errors;
        }}
        render={({
          handleSubmit,
          submitting,
          submitError,
          values,
        }) => {
          return (
            <form onSubmit={handleSubmit} noValidate>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Field name="name" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          variant="standard"
                          label={t({ id: "nameLabel" })}
                          InputLabelProps={{ required: true }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                          data-cy="nameTexxt"
                        />
                      </Grid>
                    )}
                  </Field>

                  <Field name="resourceLibraryId" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectResourceLibrary
                            {...input}
                            label={t({
                              id: "resourceLibraryFormLibraryNameLabel",
                            })}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                            required
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  <Grid item xs={12}>
                    <Field name="publicationDate" required>
                      {({ input, meta, fi }) => (
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <DatePicker
                            label={t({
                              id: "publicationDate",
                            })}
                            format={standardDateFormat}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                required: true,
                                variant: "standard",
                                error: meta.error && meta.submitFailed,
                                helperText: meta.error && meta.submitFailed && meta.error,
                              },
                              actionBar: {
                                actions: ["clear", "cancel"]
                              }
                            }}
                            {...input}
                            value={input.value === "" ? null : input.value}
                            data-cy="publicationDatePicker"
                          />
                        </FormControl>
                      )}
                    </Field>
                  </Grid>

                  <Field name="resourceLibraryTypeId" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectResourceLibraryItemType
                            {...input}
                            label={t({
                              id: "resourceLibraryFormItemTypeNameLabel",
                            })}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                            required
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  <Field name="description">
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          variant="standard"
                          label={t({ id: "descriptionLabel" })}
                          InputLabelProps={{ required: false }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                          data-cy="descriptionText"
                        />
                      </Grid>
                    )}
                  </Field>

                  <Field
                    name="sequence"
                    required
                    type="text"
                    parse={parseInteger}
                  >
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          variant="standard"
                          label={t({ id: "resourceLibraryFormSequenceLabel" })}
                          InputLabelProps={{ required: true }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                          data-cy="sequenceText"
                        />
                      </Grid>
                    )}
                  </Field>

                  <Grid item xs={12}>
                    <Box sx={styles.lineContainer}>
                      <FormLabel required>
                        <T id="uploadFileLabel" />
                      </FormLabel>
                      <Typography sx={styles.info}>
                        {values.file?.name}
                      </Typography>
                      <FileField name="file" required data-cy="uploadFileButton"/>
                    </Box>
                  </Grid>

                  {submitError && (
                    <Alert sx={styles.feedback} severity="error">
                      {submitError}
                    </Alert>
                  )}
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button disabled={submitting} onClick={onCancel} data-cy="cancelButton">
                  {t({ id: "cancelButton" })}
                </Button>

                <Button type="submit" color="primary" disabled={submitting} data-cy="saveButton">
                  {submitLabel}
                </Button>
              </DialogActions>
            </form>
          );
        }}
      />
    ),
    [
      styles.feedback,
      init,
      onCancel,
      submitLabel,
      t,
      styles.info,
      styles.lineContainer,
      isInsert,
      onInnerSubmit,
    ]
  );
};

export default ResourceLibraryItemForm;
